/**
 * Mix & Match Bundle widget (metafield products only — no main product)
 * - All bundle items are selected by default.
 * - Add to Cart stays disabled until at least `data-minimum-items` (2) are selected.
 * - Total price = sum of selected items' prices, discounted by a flat
 *   `discount_percent` (e.g. 40%) — matches Shopify's real money format
 *   for the store's actual currency (not a hardcoded $).
 * - Opens the theme's cart drawer after a successful add via .header__cart-link.
 *
 * Uses event delegation (listeners on `document`) so it keeps working even
 * if your theme re-renders part of the page's HTML without a full reload.
 */
(function () {
  console.log('[mix-match-bundle] script version v4 loaded');

  // Implements Shopify's standard money_format placeholder syntax
  // (the same logic Shopify's own money.js snippet uses), so currency
  // always matches the store's configured format (₹, $, €, etc.)
  function formatMoney(cents, format, forcedPrecision) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format && placeholderRegex.test(format) ? format : '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = typeof thousands === 'undefined' ? ',' : thousands;
      decimal = typeof decimal === 'undefined' ? '.' : decimal;

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    let value = '';

    if (typeof forcedPrecision === 'number') {
      // Ignore the store's configured decimal count (e.g. amount_no_decimals)
      // and always render with this many decimal places.
      const usesComma = match[1].indexOf('comma_separator') !== -1;
      value = usesComma
        ? formatWithDelimiters(cents, forcedPrecision, '.', ',')
        : formatWithDelimiters(cents, forcedPrecision);
    } else {
      switch (match[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
    }

    return formatString.replace(placeholderRegex, value);
  }

  function recalc(root) {
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const minimumItems = parseInt(root.dataset.minimumItems, 10) || 2;
    const checkboxes = root.querySelectorAll('[data-mmb-checkbox]');

    const totalEl = root.querySelector('[data-mmb-total]');
    const compareEl = root.querySelector('[data-mmb-compare]');
    const badgeEl = root.querySelector('[data-mmb-discount-badge]');
    const addBtn = root.querySelector('[data-mmb-add-to-cart]');
    const hintEl = root.querySelector('[data-mmb-hint]');

    if (!totalEl) {
      console.warn('[mix-match-bundle] Summary total element not found — check the section markup.');
      return;
    }

    // Total/compare/badge are now derived purely from each selected item's
    // real MRP (compare_at_price) vs its actual selling price (price) —
    // e.g. 3 items at ₹300 MRP / ₹150 selling each → ₹900 vs ₹450 → 50% OFF.
    // No artificial discount percentage is layered on top of this.
    let sellingTotal = 0;
    let compareTotal = 0;
    let selectedCount = 0;

    checkboxes.forEach((cb) => {
      if (!cb.checked) return;
      const price = parseInt(cb.dataset.price, 10) || 0;
      const compare = parseInt(cb.dataset.compare, 10) || price;
      sellingTotal += price;
      compareTotal += compare > price ? compare : price;
      selectedCount += 1;
    });

    const meetsMinimum = selectedCount >= minimumItems;
    const hasSavings = compareTotal > sellingTotal;

    totalEl.textContent = formatMoney(sellingTotal, moneyFormat, 1);

    if (compareEl) {
      if (meetsMinimum && hasSavings) {
        compareEl.textContent = formatMoney(compareTotal, moneyFormat, 1);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (badgeEl) {
      if (meetsMinimum && hasSavings) {
        const pctOff = Math.round(((compareTotal - sellingTotal) / compareTotal) * 100);
        badgeEl.textContent = pctOff + '% OFF';
        badgeEl.hidden = false;
      } else {
        badgeEl.hidden = true;
      }
    }

    if (addBtn) {
      addBtn.disabled = !meetsMinimum;
    }

    if (hintEl) {
      if (meetsMinimum) {
        hintEl.textContent = '';
        hintEl.hidden = true;
      } else {
        const remaining = minimumItems - selectedCount;
        hintEl.textContent = 'Select at least ' + minimumItems + ' items to add to cart (' + remaining + ' more needed).';
        hintEl.hidden = false;
      }
    }
  }

  function applyVariantChange(select) {
    const item = select.closest('.mmb-item');
    if (!item) return;

    const option = select.options[select.selectedIndex];
    const checkbox = item.querySelector('[data-mmb-checkbox]');
    const priceEl = item.querySelector('[data-mmb-item-price]');
    const compareEl = item.querySelector('[data-mmb-item-compare]');

    if (checkbox) {
      checkbox.dataset.variantId = option.value;
      checkbox.dataset.price = option.dataset.price;
      checkbox.dataset.compare = option.dataset.compare;
    }

    if (priceEl) priceEl.textContent = option.dataset.priceFormatted;

    if (compareEl) {
      if (option.dataset.hasCompare === 'true') {
        compareEl.textContent = option.dataset.compareFormatted;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  // This theme opens its cart drawer via the header cart icon/link.
  // Clicking it (rather than reimplementing the drawer's open logic)
  // lets the theme's own JS handle rendering and animation.
  function openCartDrawer(cart) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));

    const cartLink = document.querySelector('.header__cart-link');
    if (cartLink) {
      cartLink.click();
      return;
    }

    console.warn('[mix-match-bundle] Could not find .header__cart-link to open the cart drawer.');
  }

  async function addBundleToCart(root) {
    const mainVariantId = root.dataset.mainVariantId;
    const minimumItems = parseInt(root.dataset.minimumItems, 10) || 2;
    const addBtn = root.querySelector('[data-mmb-add-to-cart]');
    const btnText = root.querySelector('[data-mmb-btn-text]');
    const errorEl = root.querySelector('[data-mmb-error]');
    const checkboxes = root.querySelectorAll('[data-mmb-checkbox]');

    const bundleId = 'mmb-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);

    const selected = Array.from(checkboxes).filter((cb) => cb.checked);
    if (selected.length < minimumItems) {
      // Defensive check — the button should already be disabled in this case.
      return;
    }

    if (addBtn) addBtn.disabled = true;
    if (btnText) btnText.textContent = 'Adding…';
    if (errorEl) errorEl.hidden = true;

    try {
      // Check whether the main product is already sitting in the cart
      // (e.g. added separately via the normal product page button) so we
      // don't add it a second time as a duplicate line.
      let existingMainLine = null;
      const mainCheckbox = selected.find((cb) => cb.dataset.variantId === mainVariantId);

      if (mainCheckbox) {
        const currentCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        existingMainLine = currentCart.items.find(
          (item) => String(item.variant_id) === String(mainVariantId)
        );
      }

      const items = [];
      selected.forEach((cb) => {
        const isMain = cb.dataset.variantId === mainVariantId;
        if (isMain && existingMainLine) return; // don't re-add — already in cart
        items.push({
          id: cb.dataset.variantId,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_role: isMain ? 'main' : 'child',
          },
        });
      });

      if (items.length) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || 'Could not add the bundle to your cart.');
        }
      }

      // Main product was already in the cart — tag that existing line with
      // this bundle's id/role instead of adding a duplicate, so removal
      // cascade still links it to the items we just added.
      if (mainCheckbox && existingMainLine) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingMainLine.key,
            quantity: existingMainLine.quantity,
            properties: Object.assign({}, existingMainLine.properties, {
              _bundle_id: bundleId,
              _bundle_role: 'main',
            }),
          }),
        });
      }

      document.dispatchEvent(
        new CustomEvent('bundle:added', {
          detail: { bundleId, itemCount: selected.length },
        })
      );

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);

      openCartDrawer(cart);

      if (btnText) btnText.textContent = 'Added ✓';
      setTimeout(() => {
        if (btnText) btnText.textContent = 'Create Your Bundle';
        recalc(root); // restores correct disabled state (not just re-enabling)
      }, 1800);
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
      if (btnText) btnText.textContent = 'Create Your Bundle';
      recalc(root);
    }
  }

  function init() {
    const roots = document.querySelectorAll('[data-bundle]');

    if (!roots.length) {
      console.warn('[mix-match-bundle] No [data-bundle] element found — check the bundle_products metafield has values on this product.');
      return;
    }

    roots.forEach((root) => recalc(root));

    document.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-mmb-checkbox]');
      if (checkbox) {
        const root = checkbox.closest('[data-bundle]');
        if (root) recalc(root);
        return;
      }

      const select = event.target.closest('[data-mmb-variant-select]');
      if (select) {
        applyVariantChange(select);
        const root = select.closest('[data-bundle]');
        if (root) recalc(root);
      }
    });

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-mmb-add-to-cart]');
      if (!btn || btn.disabled) return;
      const root = btn.closest('[data-bundle]');
      if (root) addBundleToCart(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();